import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, 
  Upload, 
  Link, 
  X, 
  CheckCircle, 
  AlertCircle,
  Loader2
} from 'lucide-react';
import { pdfConversionService, PDFUploadProgress } from '../services/pdfConversionService';
import { TutorialStep } from '../types';
import { aiGlossaryService } from '../services/aiGlossaryService';

interface PDFImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (tutorial: {
    title: string;
    description: string;
    type: 'guide' | 'course' | 'presentation';
    difficulty: 'beginner' | 'intermediate' | 'advanced';
    estimatedTime: string;
    steps: TutorialStep[];
  }) => void;
}

const PDFImportModal: React.FC<PDFImportModalProps> = ({
  isOpen,
  onClose,
  onImport
}) => {
  const [importMethod, setImportMethod] = useState<'file' | 'url'>('file');
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfUrl, setPdfUrl] = useState('');
  const [difficulty, setDifficulty] = useState<'beginner' | 'intermediate' | 'advanced'>('beginner');
  const [estimatedTime, setEstimatedTime] = useState('15 min');
  const [contentDepth, setContentDepth] = useState<'simplified' | 'technical' | 'in-depth'>('technical');
  const [processImages, setProcessImages] = useState(true);
  const [isConverting, setIsConverting] = useState(false);
  const [progress, setProgress] = useState<PDFUploadProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      setPdfFile(file);
      setError(null);
      setSuccessMessage(null);
    } else {
      setError('Please select a valid PDF file');
    }
  };

  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPdfUrl(event.target.value);
    setError(null);
    setSuccessMessage(null);
  };

  const handleConvert = async () => {
    if (importMethod === 'file' && !pdfFile) {
      setError('Please select a PDF file');
      return;
    }

    if (importMethod === 'url' && !pdfUrl.trim()) {
      setError('Please enter a PDF URL');
      return;
    }

    setIsConverting(true);
    setError(null);
    setProgress(null);
    setSuccessMessage(null);

    try {
      let result;
      
      if (importMethod === 'file' && pdfFile) {
        result = await pdfConversionService.convertPDFToTutorial(
          pdfFile,
          (progress) => setProgress(progress),
          {
            contentDepth,
            processImages
          }
        );
      } else if (importMethod === 'url' && pdfUrl) {
        result = await pdfConversionService.convertPDFUrlToTutorial(
          pdfUrl,
          (progress) => setProgress(progress)
        );
      } else {
        throw new Error('Invalid import method or missing data');
      }

      if (result.success && result.tutorial) {
        // Use AI-estimated values if available, otherwise use user input
        onImport({
          ...result.tutorial,
          difficulty: result.tutorial.difficulty || difficulty,
          estimatedTime: result.tutorial.estimatedTime || estimatedTime
        });
        
        // Process glossary keywords if available
        let glossaryCount = 0;
        if (result.glossaryResult?.success && result.glossaryResult.keywords?.length > 0) {
          try {
            // Convert backend keywords to frontend format
            const keywords = result.glossaryResult.keywords.map((keyword: any) => ({
              term: keyword.term,
              definition: keyword.definition,
              category: keyword.category,
              context: '',
              confidence: 0.9
            }));
            
            const glossaryResult = await aiGlossaryService.addKeywordsToGlossary(keywords);
            glossaryCount = glossaryResult.addedToGlossary;
          } catch (error) {
            console.error('Failed to process glossary keywords:', error);
          }
        }

        // Show tutorial generation recap with glossary count and AI estimates
        const finalDifficulty = result.tutorial.difficulty || difficulty;
        const finalTime = result.tutorial.estimatedTime || estimatedTime;
        const detectedType = result.tutorial.type || 'guide';
        
        if (glossaryCount > 0) {
          setSuccessMessage(`✅ ${detectedType.charAt(0).toUpperCase() + detectedType.slice(1)} created successfully! Added ${glossaryCount} technical terms to the glossary. Estimated: ${finalDifficulty} level, ${finalTime}. PDF file has been automatically cleaned up.`);
        } else {
          setSuccessMessage(`✅ ${detectedType.charAt(0).toUpperCase() + detectedType.slice(1)} created successfully! Estimated: ${finalDifficulty} level, ${finalTime}. PDF file has been automatically cleaned up.`);
        }
        
        // Don't close automatically - let user see the success message and close manually
      } else if (result.success && result.draftId) {
        // Handle background processing result
        setSuccessMessage(`✅ PDF processing started in background! Your tutorial is being created and will appear in the drafts section when ready. You can close this modal and check the drafts page to monitor progress.`);
      } else {
        setError(result.error || 'Conversion failed');
      }
    } catch (error) {
      console.error('Conversion error:', error);
      setError(error instanceof Error ? error.message : 'Conversion failed');
    } finally {
      setIsConverting(false);
      setProgress(null);
    }
  };

  const handleClose = () => {
    if (!isConverting) {
      setPdfFile(null);
      setPdfUrl('');
      setError(null);
      setProgress(null);
      setSuccessMessage(null);
      onClose();
    }
  };

  const getProgressColor = (stage: string) => {
    switch (stage) {
      case 'uploading': return 'bg-blue-500';
      case 'extracting': return 'bg-green-500';
      case 'extracting-images': return 'bg-yellow-500';
      case 'analyzing-images': return 'bg-purple-500';
      case 'processing': return 'bg-yellow-500';
      case 'generating': return 'bg-purple-500';
      case 'complete': return 'bg-green-600';
      default: return 'bg-gray-500';
    }
  };

  const getStageIcon = (stage: string) => {
    switch (stage) {
      case 'uploading': return <Upload className="w-4 h-4" />;
      case 'extracting': return <FileText className="w-4 h-4" />;
      case 'extracting-images': return <FileText className="w-4 h-4 text-yellow-500" />;
      case 'analyzing-images': return <Loader2 className="w-4 h-4 animate-spin text-purple-500" />;
      case 'processing': return <Loader2 className="w-4 h-4 animate-spin text-yellow-500" />;
      case 'generating': return <Loader2 className="w-4 h-4 animate-spin text-purple-500" />;
      case 'complete': return <CheckCircle className="w-4 h-4 text-green-600" />;
      default: return <Loader2 className="w-4 h-4 animate-spin" />;
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <FileText className="w-6 h-6 text-blue-600" />
                <h2 className="text-xl font-semibold text-gray-900">
                  Import Tutorial from PDF
                </h2>
              </div>
              <button
                onClick={handleClose}
                disabled={isConverting}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 pb-4 space-y-6">
              {/* Import Method Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Choose import method
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setImportMethod('file')}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      importMethod === 'file'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <Upload className="w-5 h-5 text-gray-600" />
                      <div className="text-left">
                        <div className="font-medium text-gray-900">Upload PDF</div>
                        <div className="text-sm text-gray-500">Upload a PDF file from your computer</div>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => setImportMethod('url')}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      importMethod === 'url'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <Link className="w-5 h-5 text-gray-600" />
                      <div className="text-left">
                        <div className="font-medium text-gray-900">PDF URL</div>
                        <div className="text-sm text-gray-500">Enter a URL to an online PDF</div>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* File Upload */}
              {importMethod === 'file' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select PDF File
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                    {pdfFile ? (
                      <div className="space-y-2">
                        <FileText className="w-12 h-12 text-blue-600 mx-auto" />
                        <div className="font-medium text-gray-900">{pdfFile.name}</div>
                        <div className="text-sm text-gray-500">
                          {(pdfFile.size / 1024 / 1024).toFixed(2)} MB
                        </div>
                        <button
                          onClick={() => {
                            setPdfFile(null);
                            if (fileInputRef.current) fileInputRef.current.value = '';
                          }}
                          className="text-sm text-red-600 hover:text-red-800"
                        >
                          Remove file
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Upload className="w-12 h-12 text-gray-400 mx-auto" />
                        <div className="text-gray-600">
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            className="text-blue-600 hover:text-blue-800 font-medium"
                          >
                            Click to upload
                          </button>
                          <span className="text-gray-500"> or drag and drop</span>
                        </div>
                        <div className="text-sm text-gray-500">PDF files only</div>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="application/pdf"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </div>
                </div>
              )}

              {/* URL Input */}
              {importMethod === 'url' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    PDF URL
                  </label>
                  <input
                    type="url"
                    value={pdfUrl}
                    onChange={handleUrlChange}
                    placeholder="https://example.com/document.pdf"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Enter the URL of a PDF document hosted online
                  </p>
                </div>
              )}

              {/* Difficulty and Time Estimation */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Difficulty
                  </label>
                  <select
                    value={difficulty}
                    onChange={(e) => setDifficulty(e.target.value as 'beginner' | 'intermediate' | 'advanced')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Time
                  </label>
                  <input
                    type="text"
                    value={estimatedTime}
                    onChange={(e) => setEstimatedTime(e.target.value)}
                    placeholder="15 min"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Content Depth and Image Processing Options */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Content Depth
                  </label>
                  <select
                    value={contentDepth}
                    onChange={(e) => setContentDepth(e.target.value as 'simplified' | 'technical' | 'in-depth')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="simplified">Simplified - Easy to understand</option>
                    <option value="technical">Technical - Professional level</option>
                    <option value="in-depth">In-depth - Comprehensive coverage</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Image Processing
                  </label>
                  <div className="flex items-center space-x-2">
                    <div className="relative">
                      <input
                        type="checkbox"
                        id="processImages"
                        checked={processImages}
                        onChange={(e) => setProcessImages(e.target.checked)}
                        className="sr-only"
                      />
                      <label
                        htmlFor="processImages"
                        className={`flex items-center justify-center w-4 h-4 border-2 rounded cursor-pointer transition-colors ${
                          processImages
                            ? 'bg-blue-600 border-blue-600 text-white'
                            : 'bg-white border-gray-300 text-transparent'
                        }`}
                      >
                        {processImages && (
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </label>
                    </div>
                    <label htmlFor="processImages" className="text-sm text-gray-700 cursor-pointer">
                      Process images (slower but includes visuals)
                    </label>
                  </div>
                </div>
              </div>

              {/* Progress */}
              {progress && (
                <div className="space-y-4">
                  {/* Main Progress Bar */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">
                        {progress.message}
                      </span>
                      <span className="text-sm text-gray-500 font-mono">
                        {progress.progress}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full transition-all duration-500 ${getProgressColor(progress.stage)}`}
                        style={{ width: `${progress.progress}%` }}
                      />
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      {getStageIcon(progress.stage)}
                      <span className="capitalize font-medium">{progress.stage}</span>
                    </div>
                  </div>

                  {/* Detailed Progress Information */}
                  {progress.details && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                        <span className="text-sm font-medium text-blue-800">
                          Current Step: {progress.details.currentStage || 'Processing...'}
                        </span>
                      </div>
                      
                      {/* Image Analysis Progress */}
                      {progress.details.totalImages && progress.details.totalImages > 0 && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-blue-700">
                              📸 Analyzing images: {progress.details.currentImage || 0} of {progress.details.totalImages}
                            </span>
                            <span className="text-blue-600 font-mono">
                              {progress.details.currentBatch && progress.details.totalBatches 
                                ? `Batch ${progress.details.currentBatch}/${progress.details.totalBatches}`
                                : ''
                              }
                            </span>
                          </div>
                          <div className="w-full bg-blue-200 rounded-full h-2">
                            <div
                              className="h-2 bg-blue-500 rounded-full transition-all duration-300"
                              style={{ 
                                width: `${progress.details.currentImage && progress.details.totalImages 
                                  ? (progress.details.currentImage / progress.details.totalImages) * 100 
                                  : 0}%` 
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Image Statistics */}
                      {(progress.details.extractedImages || progress.details.educationalImages || progress.details.filteredImages) && (
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          {progress.details.extractedImages && (
                            <div className="text-center">
                              <div className="text-blue-600 font-semibold">{progress.details.extractedImages}</div>
                              <div className="text-blue-500">Extracted</div>
                            </div>
                          )}
                          {progress.details.educationalImages && (
                            <div className="text-center">
                              <div className="text-green-600 font-semibold">{progress.details.educationalImages}</div>
                              <div className="text-green-500">Educational</div>
                            </div>
                          )}
                          {progress.details.filteredImages && (
                            <div className="text-center">
                              <div className="text-orange-600 font-semibold">{progress.details.filteredImages}</div>
                              <div className="text-orange-500">Filtered</div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Stage-specific messages */}
                  {progress.stage === 'extracting-images' && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                        <span className="text-sm text-yellow-800">
                          🔍 Using AI to identify valuable images (charts, diagrams, photos) and filter out logos/styling
                        </span>
                      </div>
                    </div>
                  )}

                  {progress.stage === 'analyzing-images' && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                        <span className="text-sm text-purple-800">
                          🤖 AI is analyzing each image to determine educational value...
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

                     {/* Error */}
                     {error && (
                       <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                         <AlertCircle className="w-5 h-5 text-red-600" />
                         <span className="text-sm text-red-700">{error}</span>
                       </div>
                     )}

                     {/* Success Message */}
                     {successMessage && (
                       <div className="flex items-center space-x-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                         <CheckCircle className="w-5 h-5 text-green-600" />
                         <span className="text-sm text-green-700">{successMessage}</span>
                       </div>
                     )}

                     {/* Info */}
                     <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg p-4">
                       <div className="flex items-start space-x-3">
                         <div className="w-5 h-5 text-blue-600 mt-0.5">📚</div>
                         <div className="text-sm text-blue-800">
                           <p className="font-medium mb-1">PDF to Tutorial Conversion:</p>
                           <ul className="space-y-1 text-blue-700">
                             <li>• 📄 Extract text and structure from your PDF</li>
                             <li>• 🔍 Analyze content and identify key concepts</li>
                             <li>• 📝 Generate organized tutorial steps</li>
                             <li>• 🔑 Extract technical terms for glossary</li>
                             <li>• ✏️ You can edit and refine the generated content</li>
                           </ul>
                         </div>
                       </div>
                     </div>
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={handleClose}
                disabled={isConverting}
                className={`px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 ${successMessage ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {successMessage ? 'Close' : 'Cancel'}
              </button>
                     {successMessage ? (
                       <button
                         onClick={handleClose}
                         className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                       >
                         <CheckCircle className="w-4 h-4" />
                         <span>Close</span>
                       </button>
                     ) : (
                       <button
                         onClick={handleConvert}
                         disabled={isConverting || (importMethod === 'file' && !pdfFile) || (importMethod === 'url' && !pdfUrl.trim())}
                         className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                       >
                         {isConverting ? (
                           <>
                             <Loader2 className="w-4 h-4 animate-spin" />
                             <span>Converting...</span>
                           </>
                         ) : (
                           <>
                             <FileText className="w-4 h-4" />
                             <span>Convert to Tutorial</span>
                           </>
                         )}
                       </button>
                     )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default PDFImportModal;
